import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {debounceTime, filter, map, shareReplay, startWith, takeUntil, tap} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {ServerDataSource} from '@mominsamir/ngx-smart-table';
import {PrevButtonComponent} from '@theme/components/tables/components/pager-button/prev-button/prev-button.component';
import {NextButtonComponent} from '@theme/components/tables/components/pager-button/next-button/next-button.component';
import {GeneralOption} from '@core/interfaces/system/system-common';
import {NestedSelectDropdown} from '@theme/components/ui/nested-select-dropdown/nested-select-dropdown.component';
import {MultiselectOption} from '@theme/components';

export interface ColumnConfig {
    title: string;
    width?: string;
    sort?: boolean;
    sortDirection?: 'asc' | 'desc';
    type: 'string' | 'number' | 'custom' | 'date';
    renderComponent?: any;
    onComponentInitFunction?: (instance: any) => void;
    valuePrepareFunction?: (value: any) => string;
    filterOperator?: string;
    filter?:
        | {
              type: 'custom' | 'default';
              component: any;
              config?: Record<string, any>;
          }
        | boolean;
    groupBy?: boolean;
    isScrollable?: boolean;
}

export type ColumnsConfigMap = Record<string, ColumnConfig>;

export interface CustomButton {
    enabled: boolean;
    label: string;
    icon?: string;
    pack?: string;
    status?: string;
    callback: () => void;
}

export interface AdditionalBlock {
    enable: Observable<boolean>; // whether the block is visible
    toggleCallback?: () => void; // called by the close button
    additionalBlockTitle?: AdditionalBlockTitle; // title shown inside the block
    additionalBlockButtons?: AdditionalBlockSimpleButton[]; // simple action buttons
    additionalBlockDropdown?: AdditionalBlockDropdown; // dropdown + confirm
    nestedSelectDropdown?: NestedSelectDropdown; // optional nested select UI
}

export type AdditionalBlockTitle = Observable<string>;

export interface AdditionalBlockButton {
    enable: Observable<boolean>;
    label: string;
    icon?: string;
    pack?: string;
    outline?: boolean;
    status?: string;
}

export interface AdditionalBlockSimpleButton extends AdditionalBlockButton {
    callback: () => {};
}

export interface AdditionalBlockDropdown {
    enable: Observable<boolean>;
    label: string;
    actionBtnIcon: string;
    pack?: string;
    callback: (data: any) => void;
    optionsList$: Observable<GeneralOption[]>;
}

export interface TableFilter {
    field: string;
    search: string;
    filter: any;
    operator: string;
}
export interface TableSort {
    field: string;
    direction: string;
    compare: any;
}
export interface FiltersChanges {
    current: TableFilter;
    allFilters: TableFilter[];
}

export interface DownloadBulkFiles {
    value: string;
    filterParams: string;
}

export interface MultiselectOptionExtension {
    fieldGroup?: string;
    disabled: boolean;
    isSelected?: boolean;
    columnTitle?: string;
    columnKey?: string;
    visible: boolean;
}

export interface MultiselectOptionExtended extends MultiselectOption<MultiselectOptionExtension> {}

export interface SelectColumnBtn {
    enable: boolean;
    columns$: Observable<MultiselectOptionExtended[]>;
}

@Component({
    selector: 'ngx-data-table',
    templateUrl: './data-table-component.html',
    styleUrls: ['./data-table-component.scss'],
    changeDetection: ChangeDetectionStrategy.Default, //needs to be Default due to limitations with the OnPush strategy from the lib
})
export class DataTableComponent extends Unsubscribable implements OnInit, OnChanges {
    @Input() settings: any;
    @Input() title: string;
    @Input() subTitle: string;
    //How to use initFilter and how to visually pre-fill the input filters:
    //See: https://engineeredintelligence.myjetbrains.com/youtrack/articles/ENGIN-A-485/Initial-Table-Filtering-ngx-data-table
    @Input() initFilter: TableFilter[] = [];
    @Input() reload: Observable<number>;
    @Input() source: Observable<ServerDataSource> = new BehaviorSubject<ServerDataSource>(null);
    @Output() onCustomEvent = new EventEmitter();
    @Output() userRowSelect = new EventEmitter();
    @Output() rowSelect = new EventEmitter();
    @Output() onFilterChange: EventEmitter<FiltersChanges> = new EventEmitter();
    @Output() downloadFile: EventEmitter<string> = new EventEmitter();
    @Output() downloadBulkFiles: EventEmitter<DownloadBulkFiles> = new EventEmitter();
    @Output() columnChanges: EventEmitter<{columns: any[]; perPage: number}> = new EventEmitter();

    public data: ServerDataSource;
    public emptyData: boolean;
    public disableRefresh: boolean = false;

    public pagingFormGroup = this.fb.group({
        perPage: this.fb.control(8),
    });

    readonly perPage$: Observable<number> = this.perPageFormControl.valueChanges.pipe(
        startWith(this.pagingFormGroup.value.perPage),
        tap(() => (this.disableRefresh = true)),
        debounceTime(200),
        map(() => {
            const value = this.pagingFormGroup.value.perPage;

            let result: number;
            if (value < 1) {
                this.perPageFormControl.setValue(1, {emitEvent: false});
            }
            if (value > 99) {
                this.perPageFormControl.setValue(99, {emitEvent: false});
            }

            if (value > 99) result = 99;
            else if (value < 1) result = 1;
            else result = value;

            this.disableRefresh = false;
            return result;
        }),
    );

    _selectedColumnsControl: FormControl = new FormControl([]);

    constructor(protected fb: FormBuilder, private cdr: ChangeDetectorRef) {
        super();
    }

    get headerHide(): SelectColumnBtn {
        return this.settings.headerHide || false;
    }

    get selectColumnBtn(): SelectColumnBtn {
        return this.settings.selectColumnBtn || null;
    }

    get customButton(): CustomButton[] | null {
        return this.settings.customButton || null;
    }

    get additionalBlock(): AdditionalBlock {
        return this.settings.additionalBlock || {enable: of(false)};
    }

    get additionalBlockButtons(): AdditionalBlockSimpleButton[] {
        return this.settings.additionalBlock?.additionalBlockButtons || [];
    }

    additionalBlockDropdownControl: FormControl = new FormControl();

    get additionalBlockDropdown(): AdditionalBlockDropdown | null {
        return this.settings.additionalBlock?.additionalBlockDropdown || null;
    }

    get additionalBlockTitle(): AdditionalBlockTitle | null {
        return this.settings.additionalBlock?.additionalBlockTitle || null;
    }

    get nestedSelectDropdown(): NestedSelectDropdown {
        return this.settings.additionalBlock?.nestedSelectDropdown || null;
    }

    private get perPageFormControl(): FormControl {
        return this.pagingFormGroup.get('perPage') as FormControl;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.settings?.currentValue) {
            if (this.settings.pager.mode === 'custom') {
                this.settings.pager = {
                    ...this.settings.pager,
                    prevComponent: PrevButtonComponent,
                    nextComponent: NextButtonComponent,
                };
            }
            // Reset records per page based on settings, if defined
            if (this.settings.pager && this.settings.pager.perPage) {
                this.setPerPageFormControl(this.settings.pager.perPage);
            }
        }
    }

    onCustomEventClick($event): void {
        this.onCustomEvent.emit($event);
    }

    refresh = () => {
        if (this.data) this.data.refresh();
    };

    onSearch(query: string = '') {
        Object.keys(this.settings.columns)
            .filter((k) => this.settings.columns[k].type === 'string')
            .map((k) => ({field: k + '_G', search: query}));
    }

    enableHeader() {
        if (this.settings) {
            return this.settings['enableHeader'] === undefined ? true : this.settings['enableHeader'];
        }
    }

    enableGlobalSearch = () => this.settings['enableGlobalSearch'];

    enablePagePerRecord = () =>
        this.settings['enablePagePerRecord'] === undefined ? false : this.settings['enablePagePerRecord'];

    /**
     *  Enable refresh in table header.
     *  Past implementation had this controlled via enablePagePerRecord(), so these settings are currently linked.
     *  TODO: refactor to enabled by default, disabled by exception, and decouple from enablePagePerRecord.
     */
    enableTableRefresh = () => this.settings['enableTableRefresh'] || this.enablePagePerRecord();

    ngOnInit(): void {
        combineLatest<Observable<ServerDataSource>, Observable<number>>([this.source, this.perPage$])
            .pipe(
                takeUntil(this.unsubscribe$),
                tap((dataSource) => {
                    if (dataSource[0] === null) this.data = null;
                }),
                filter((dataSource) => dataSource[0] !== null && dataSource[1] > 0),
                map(([source, perPage]: [ServerDataSource, number]) => {
                    const paging = source.getPaging();
                    if (!paging.page) {
                        source.setPaging(1, perPage, false);
                    } else if (source.getPaging().perPage !== perPage) {
                        // this block executed when per page records are changed.
                        source.setPaging(paging.page, perPage, false);
                    }
                    if (this.initFilter?.length > 0) {
                        // this sets initial filters
                        this.initFilter.forEach((x) => source.addFilter(x, false));
                    }
                    return source;
                }),
                debounceTime(500),
                shareReplay(1),
            )
            .subscribe((data) => {
                this.data = data;
                // Subscribe to changes in the data source and manually trigger change detection.
                // This workaround is necessary because the current table library does not internally support proper update mechanisms when a parent uses the OnPush change detection strategy.
                // A more elegant solution would require a revision of how the table library handles data updates and rendering under OnPush conditions.
                // Due to limitations with the OnPush strategy on a parent component, we are forced to use two levels of manual updates:
                this.data
                    .onChanged()
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe((tableData) => {
                        this.emptyData = tableData.elements.length === 0;
                        // This check is to avoid the table from flashing an empty state when the page is filled with values from a previous page
                        if (this.emptyData && tableData.action === 'refresh') return;
                        // 1. Immediately after data changes, we call `detectChanges()` to refresh the rows, particularly when the table is empty.
                        this.cdr.detectChanges();
                        // 2. Then, after a small timeout (100ms), we call `detectChanges()` again to ensure that the custom empty state component is rendered correctly.
                        setTimeout(() => {
                            this.cdr.detectChanges();
                        }, 100);
                    });

                this.cdr.detectChanges();
            });
        this.reload
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((data) => data !== null),
            )
            .subscribe((_) => {
                /*
                 * TODO: this clears filters from the data source, but not from the table itself.
                 *  As such we end up with table data not matching the filters from user.
                 *  .reset() also does not have the desired effect.
                 *  Removed for now.
                 */
                //if (this.data) this.data.setFilter([]); //((source) => source.setFilter([]))

                if (this.data) this.data.refresh();
            });

        if (this.selectColumnBtn) {
            this.settings.selectColumnBtn.columns$
                .pipe(
                    takeUntil(this.unsubscribe$),
                    tap((columns: any[]) => {
                        let _visible = columns.filter((_c) => _c.option.visible);
                        this._selectedColumnsControl.setValue(_visible.map((item) => item.option));
                    }),
                )
                .subscribe();
            this._selectedColumnsControl.valueChanges
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((value) =>
                    this.columnChanges.emit({columns: value, perPage: this.perPageFormControl.value}),
                );
        }
    }

    private setPerPageFormControl(val: number) {
        this.perPageFormControl.reset(val);
    }

    enableTableSetting = () =>
        !this.settings['tableSettings'] || this.settings['tableSettings'] === undefined
            ? false
            : this.settings['tableSettings'];

    enableCustomCsvDownloadMenu = () =>
        !this.settings['customCsvDownloadMenu'] || this.settings['customCsvDownloadMenu'] === undefined
            ? null
            : this.settings['customCsvDownloadMenu'];

    get showPlain() {
        return !this.settings['showPlain'] || this.settings['showPlain'] === undefined
            ? false
            : this.settings['showPlain'];
    }

    onRowSelect = ($event) => this.rowSelect.emit($event);
    onUserRowSelect = ($event) => this.userRowSelect.emit($event);

    onFilter(current: TableFilter) {
        const allFilters: TableFilter[] = this.data.getFilter().filters;

        this.onFilterChange.emit({current, allFilters});
    }

    onDownload() {
        this.downloadFile.emit(this.filterParamsString);
    }
    onBulkDownload(value) {
        this.downloadBulkFiles.emit({value, filterParams: this.filterParamsString});
    }

    get filterParamsString(): string {
        let str = '';
        const sort: TableSort[] = this.data.getSort();
        const filters: TableFilter[] = this.data.getFilter().filters;

        if (sort.length) {
            const sortStr = sort
                .map((item) => {
                    return `sortBy=${encodeURIComponent(item.field)}&orderBy=${encodeURIComponent(
                        item.direction.toUpperCase(),
                    )}`;
                })
                .join('&');
            str += sortStr;
        }

        if (filters.length) {
            const filtersStr = filters
                .filter((item) => item.field !== undefined && item.operator !== undefined && item.search !== '')
                .map((item) => {
                    return `filterBy${encodeURIComponent(item.field)}_${item.operator}=${encodeURIComponent(
                        item.search,
                    )}`;
                })
                .join('&');
            if (str.length) str += '&';
            str += filtersStr;
        }
        return str;
    }
}
